// react
import { ChangeEvent, useRef, useState } from 'react';

// types
import { AiDocument, DataRequestField } from '../../../@types';

// mui
import { Box, Button, Chip, FormHelperText, IconButton, Stack, Typography } from '@mui/material';

// icons
import {
	Close as IconClose,
	LibraryAddCheckOutlined as IconPick,
	UploadFile as IconUploadFile,
} from '@mui/icons-material';

// dialogs
import {
	DialogDocumentPicker,
	PickedDocument,
} from '../../dialogs/DialogDocumentPicker/DialogDocumentPicker';

// props
export interface UploadDocProps {
	field: DataRequestField;
	disabled: boolean;
	readOnly: boolean;
	error?: string | null;
	uploadedDocumentIds?: string[];
	uploadedDocumentNames?: string[];
	onUpload?: (file: File) => Promise<{ documentId: string; documentName: string }>;
	onRemoveDocument?: (documentId: string) => void;
	/** Context documents that can be attached without re-uploading. */
	documents?: AiDocument[];
	/** Apply already-uploaded documents (from the picker) to this field. */
	onSelectExisting?: (docs: { documentId: string; documentName: string }[]) => void;
}

export const UploadDoc = ({
	field,
	disabled,
	readOnly,
	error,
	uploadedDocumentIds = [],
	uploadedDocumentNames = [],
	onUpload,
	onRemoveDocument,
	documents,
	onSelectExisting,
}: UploadDocProps) => {
	const inputRef = useRef<HTMLInputElement | null>(null);
	const [isUploading, setIsUploading] = useState(false);
	const [localError, setLocalError] = useState<string | null>(null);
	const [pickerOpen, setPickerOpen] = useState(false);

	const max = field.maxDocuments;
	const atCapacity = max != null && uploadedDocumentIds.length >= max;
	const isSingle = max === 1;
	const canPickExisting =
		!readOnly && !disabled && !atCapacity && !!onSelectExisting && !!documents?.length;

	const handlePicked = (picked: PickedDocument[]) => {
		if (!onSelectExisting) return;
		onSelectExisting(picked.map((p) => ({ documentId: p.id, documentName: p.filename })));
	};

	const handleChange = async (e: ChangeEvent<HTMLInputElement>) => {
		const files = Array.from(e.target.files || []);
		e.target.value = '';
		if (!onUpload || files.length === 0) return;
		setIsUploading(true);
		setLocalError(null);
		try {
			for (const f of files) {
				if (max != null && uploadedDocumentIds.length + 1 > max) break;
				await onUpload(f);
			}
		} catch (err) {
			setLocalError(err instanceof Error ? err.message : 'Upload failed');
		} finally {
			setIsUploading(false);
		}
	};

	return (
		<Box>
			<Typography variant="body2" fontWeight={600} sx={{ mb: 0.5 }}>
				{field.label}
				{field.required === true ? ' *' : ''}
			</Typography>
			<Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" rowGap={0.5}>
				{!readOnly && (
					<Button
						component="label"
						size="small"
						variant="outlined"
						startIcon={<IconUploadFile />}
						disabled={disabled || isUploading || atCapacity}>
						{isSingle && uploadedDocumentIds.length > 0 ? 'Replace' : 'Upload'}
						<input ref={inputRef} type="file" hidden multiple={!isSingle} onChange={handleChange} />
					</Button>
				)}
				{canPickExisting && (
					<Button
						size="small"
						variant="text"
						startIcon={<IconPick />}
						disabled={isUploading}
						onClick={() => setPickerOpen(true)}>
						Select Existing
					</Button>
				)}
				{uploadedDocumentIds.map((id, idx) => (
					<Chip
						key={id}
						size="small"
						label={uploadedDocumentNames[idx] || `#${id}`}
						onDelete={
							readOnly || disabled || !onRemoveDocument ? undefined : () => onRemoveDocument(id)
						}
						deleteIcon={
							<IconButton size="small">
								<IconClose fontSize="inherit" />
							</IconButton>
						}
					/>
				))}
				{!readOnly && max != null && (
					<Typography variant="caption" color="text.secondary">
						{uploadedDocumentIds.length} / {max}
					</Typography>
				)}
				{isUploading && (
					<Typography variant="caption" color="text.secondary">
						Uploading…
					</Typography>
				)}
			</Stack>
			{(error || localError) && <FormHelperText error>{error || localError}</FormHelperText>}
			{canPickExisting && (
				<DialogDocumentPicker
					isOpen={pickerOpen}
					onClose={() => setPickerOpen(false)}
					onConfirm={handlePicked}
					documents={documents || []}
					maxSelections={max ?? 1}
					currentSelections={uploadedDocumentIds}
				/>
			)}
		</Box>
	);
};
